import React from 'react';
import {
  BarChart3,
  Phone,
  Menu,
  X,
  Wifi,
  WifiOff,
  Calendar,
  Settings as SettingsIcon,
  Plug,
  RefreshCw,
  MessageSquare,
  LogOut,
  PhoneCall,
} from 'lucide-react';
import ThemeToggle from '../ui/ThemeToggle';

const AppShell = ({
  activeTab,
  onTabChange,
  isConnected,
  health,
  onRefresh,
  onTestCall,
  isRefreshing,
  isAdmin = false,
  onLogout,
  children,
}) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const isDev = process.env.NODE_ENV !== 'production';

  const navigation = [
    { id: 'dashboard', name: 'Özet Panel', icon: BarChart3 },
    { id: 'call-logs', name: 'Çağrı Kayıtları', icon: Phone },
    ...(isAdmin
      ? [{ id: 'conversation-details', name: 'Görüşme Detayları', icon: MessageSquare, subItem: true }]
      : []),
    { id: 'appointments', name: 'Randevular', icon: Calendar },
    { id: 'settings', name: 'Ayarlar', icon: SettingsIcon },
    { id: 'integrations', name: 'Entegrasyon', icon: Plug },
    { id: 'contact', name: 'İletişim', icon: PhoneCall },
  ];

  const activeName = navigation.find((n) => n.id === activeTab)?.name;

  return (
    <div className="flex h-screen text-slate-200" style={{ background: 'var(--bg-app)' }}>
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="fixed inset-0 bg-black/60" />
        </div>
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-white/[0.07] transition-transform duration-300 lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: 'var(--bg-sidebar)' }}
      >
        <div className="flex h-16 items-center justify-between border-b border-white/[0.07] px-5">
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ background: '#38806f' }}
            >
              <Phone className="h-5 w-5" style={{ color: '#ffffff' }} />
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-wide text-white">Mengerler</h1>
              <p className="text-xs text-slate-500">Randevu Asistanı</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="rounded-lg p-2 text-slate-500 hover:bg-white/[0.05] hover:text-white lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-white/[0.07] px-5 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isConnected ? (
                <Wifi className="h-4 w-4 text-emerald-400" />
              ) : (
                <WifiOff className="h-4 w-4 text-rose-400" />
              )}
              <span className={`text-sm font-medium ${isConnected ? 'text-emerald-300' : 'text-rose-300'}`}>
                {isConnected ? 'Sunucu bağlı' : 'Bağlantı yok'}
              </span>
            </div>
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-white/[0.05] hover:text-white"
                title="Verileri yenile"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
          {health?.storageDriver && (
            <p className="mt-1 text-xs text-slate-600">
              Veri: {health.storageDriver}
              {health.totalAppointments != null && ` · ${health.totalAppointments} randevu`}
            </p>
          )}
        </div>

        <nav className="flex-1 space-y-0.5 px-3 py-4">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = activeTab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onTabChange(item.id);
                  setSidebarOpen(false);
                }}
                className={`sidebar-item w-full text-left ${active ? 'active' : ''} ${
                  item.subItem ? 'ml-3 w-[calc(100%-0.75rem)] text-[13px]' : ''
                }`}
              >
                <Icon className={`${item.subItem ? 'mr-2 h-4 w-4' : 'mr-3 h-5 w-5'}`} />
                {item.name}
              </button>
            );
          })}
        </nav>

        <div className="border-t border-white/[0.07] p-4 space-y-2">
          {isDev && onTestCall && (
            <button type="button" onClick={onTestCall} className="btn-secondary w-full text-sm py-2">
              Örnek çağrı ekle
            </button>
          )}
          {onLogout && (
            <button
              type="button"
              onClick={onLogout}
              className="btn-secondary flex w-full items-center justify-center gap-2 text-sm py-2"
            >
              <LogOut className="h-4 w-4" />
              Çıkış Yap
            </button>
          )}
          <p className="text-center text-xs text-slate-600">
            Modül 3 · {isAdmin ? 'Yönetici' : 'Kullanıcı'}
          </p>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header
          className="flex h-14 items-center justify-between border-b border-white/[0.07] px-4 lg:hidden"
          style={{ background: 'var(--bg-sidebar)' }}
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="rounded-lg p-2 text-slate-400 hover:bg-white/[0.05] hover:text-white"
          >
            <Menu className="h-6 w-6" />
          </button>
          <span className="text-sm font-semibold text-white">{activeName}</span>
          <div className="flex items-center gap-3">
            {isConnected ? (
              <Wifi className="h-5 w-5 text-emerald-400" />
            ) : (
              <WifiOff className="h-5 w-5 text-rose-400" />
            )}
            <ThemeToggle className="h-8 w-8" />
          </div>
        </header>

        <main className="relative flex-1 overflow-auto">
          <div className="fixed right-5 top-4 z-40 hidden lg:block">
            <ThemeToggle />
          </div>
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppShell;
